import React, { useEffect, useMemo } from "react";
import { useMap } from "react-leaflet";
import GenericMap from "../../../shared/components/common/GenericMap";
import L from "leaflet";
import HeatmapLayer from "./HeatmapLayer";
import MapCenter from "./MapCenter";
import { getDataScale } from "../../../styles/dataColors.js";

/* Información
 * Componente DensityMap - Muestra un mapa de calor y resumen por zonas
 *
 * @param {Object} props
 * @param {Array} props.data - Array de tareas o puntos
 * @param {Array|Object} props.center - Centro del mapa ([lat, lng] o {lat, lng})
 * @param {number} props.zoom - Zoom inicial
 * @param {number} props.radius - Radio de la capa de calor
 * @param {number} props.opacity - Opacidad mínima de la capa de calor
 *
 * Formatos soportados en data:
 * - Punto: { lat, lng, count? }
 * - Punto: [lat, lng, intensidad?]
 * - Tarea: { client: { lat/lng }, status? }
 */

const toNumber = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const getPointFromItem = (item) => {
    if (!item) return null;

    if (Array.isArray(item) && item.length >= 2) {
        const lat = toNumber(item[0]);
        const lng = toNumber(item[1]);
        const intensity = toNumber(item[2]) ?? 1;
        if (lat === null || lng === null) return null;
        return { lat, lng, intensity, status: null };
    }

    const directLat = toNumber(item.fakeLat ?? item.lat ?? item.latitude ?? item.clientLat);
    const directLng = toNumber(item.fakeLng ?? item.lng ?? item.longitude ?? item.clientLng);
    if (directLat !== null && directLng !== null) {
        return {
            lat: directLat,
            lng: directLng,
            intensity: toNumber(item.count) ?? 1,
            status: item.status ?? null
        };
    }

    const clientLat = toNumber(item.client?.fakeLat ?? item.client?.lat);
    const clientLng = toNumber(item.client?.fakeLng ?? item.client?.lng);
    if (clientLat !== null && clientLng !== null) {
        return {
            lat: clientLat,
            lng: clientLng,
            intensity: toNumber(item.count) ?? 1,
            status: item.status ?? null
        };
    }

    return null;
};

const isCompletedStatus = (status) =>
    status === "completada" || status === "finalizada" || status === "finished";

const isPendingStatus = (status) =>
    status === "pendiente" || status === "pending";

const isInProgressStatus = (status) =>
    status === "en_camino" || status === "iniciada" || status === "started";

const MapBoundsControl = ({ maxBounds }) => {
    const map = useMap();

    useEffect(() => {
        if (!maxBounds) return;
        map.setMaxBounds(maxBounds);
        const minZoom = map.getBoundsZoom(maxBounds, true);
        map.setMinZoom(minZoom);
    }, [maxBounds, map]);

    return null;
};

const DensityMap = ({
    data = [],
    center = [-31.5378028, -68.5245864],
    zoom = 12,
    radius = 25,
    opacity = 0.7
}) => {
    const normalizedCenter = Array.isArray(center)
        ? { lat: Number(center[0]), lng: Number(center[1]) }
        : { lat: Number(center?.lat), lng: Number(center?.lng) };
    const maxBounds = L.latLng(normalizedCenter.lat, normalizedCenter.lng).toBounds(120000);

    const [scaleLow, , scaleMid, scaleHigh, scaleTop] = getDataScale();

    const points = useMemo(() => data.map(getPointFromItem).filter(Boolean), [data]);

    const heatmapData = useMemo(
        () => points.map((point) => [point.lat, point.lng, point.intensity ?? 1]),
        [points]
    );

    const zoneStats = useMemo(() => {
        const zoneData = {};

        points.forEach((point) => {
            const latZone = Math.round(point.lat * 100) / 100;
            const lngZone = Math.round(point.lng * 100) / 100;
            const zoneKey = `${latZone},${lngZone}`;

            if (!zoneData[zoneKey]) {
                zoneData[zoneKey] = {
                    lat: latZone,
                    lng: lngZone,
                    count: 0,
                    completed: 0,
                    pending: 0,
                    inProgress: 0
                };
            }

            zoneData[zoneKey].count += 1;

            if (isCompletedStatus(point.status)) {
                zoneData[zoneKey].completed += 1;
            } else if (isPendingStatus(point.status)) {
                zoneData[zoneKey].pending += 1;
            } else if (isInProgressStatus(point.status)) {
                zoneData[zoneKey].inProgress += 1;
            }
        });

        return Object.values(zoneData)
            .map((zone) => {
                let densidad = "baja";
                if (zone.count >= 10) densidad = "alta";
                else if (zone.count >= 5) densidad = "media";
                else if (zone.count >= 3) densidad = "media-baja";

                return {
                    ...zone,
                    densidad,
                    zona: `Zona ${zone.lat.toFixed(2)}, ${zone.lng.toFixed(2)}`,
                    completionRate: zone.count > 0 ? Math.round((zone.completed / zone.count) * 100) : 0
                };
            })
            .sort((a, b) => b.count - a.count);
    }, [points]);

    return (
        <div className="bg-surface rounded-xl shadow-lg border border-line p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-content">Mapa de Calor - Densidad de Tareas</h3>
            </div>

            <div className="w-full bg-surface-2 rounded-lg overflow-hidden h-96 mb-4">
                <GenericMap
                    center={center}
                    zoom={zoom}
                    maxZoom={17} // Limita el acercamiento para que no desaparezca el mapa
                    minZoom={3} // Opcional: evita alejarse demasiado
                    maxBounds={maxBounds}
                    maxBoundsViscosity={1.0}
                    style={{ height: "100%", width: "100%" }}
                    className="rounded-lg"
                >
                    <MapBoundsControl maxBounds={maxBounds} />
                    <MapCenter center={center} />
                    <HeatmapLayer data={heatmapData} radius={radius} opacity={opacity} />
                </GenericMap>
            </div>

            <div className="flex w-full justify-center">
                <div className="w-full max-w-full rounded-lg bg-surface-2 p-3 md:flex md:w-auto md:max-w-none md:items-center md:gap-4 md:p-2">
                    <h4 className="mb-3 text-sm font-medium text-content whitespace-nowrap md:mb-0 md:mr-2">Leyenda de Densidad:</h4>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-2 md:flex md:items-center md:gap-4">
                        <div className="flex min-w-0 items-center gap-2">
                            <div className="h-4 w-4 flex-shrink-0 rounded-full" style={{ backgroundColor: scaleTop }}></div>
                            <span className="text-xs leading-tight text-content-muted">Alta (10+)</span>
                        </div>
                        <div className="flex min-w-0 items-center gap-2">
                            <div className="h-4 w-4 flex-shrink-0 rounded-full" style={{ backgroundColor: scaleHigh }}></div>
                            <span className="text-xs leading-tight text-content-muted">Media (5-9)</span>
                        </div>
                        <div className="flex min-w-0 items-center gap-2">
                            <div className="h-4 w-4 flex-shrink-0 rounded-full" style={{ backgroundColor: scaleMid }}></div>
                            <span className="text-xs leading-tight text-content-muted">Media-baja (3-4)</span>
                        </div>
                        <div className="flex min-w-0 items-center gap-2">
                            <div className="h-4 w-4 flex-shrink-0 rounded-full" style={{ backgroundColor: scaleLow }}></div>
                            <span className="text-xs leading-tight text-content-muted">Baja (1-2)</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DensityMap;
